import React, { useMemo, useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSyncAlt,
  faUsers,
  faFilter,
  faTimesCircle,
  faFileImport,
  faFileExport,
  faPlus,
  faUserPlus,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../components/common/SearchableSelect';
import CustomersTable from '../components/customers/CustomersTable';
import CustomerForm from '../components/customers/CustomerForm';
import CustomerWarehouseForm from '../components/customers/CustomerWarehouseForm';
import CustomerWarehouseListModal from '../components/customers/CustomerWarehouseListModal';
import CustomerImportModal from '../components/customers/CustomerImportModal';
import CustomerInviteModal from '../components/customers/CustomerInviteModal';
import CustomerApprovalsTab from '../components/customers/CustomerApprovalsTab';
import CustomerDetails from '../components/customers/CustomerDetails';
import MessageAlert from '../components/common/MessageAlert';
import ActionToolbar from '../components/common/ActionToolbar';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useErrorDialog } from '../context/errorDialogContext';
import { UI_COLORS, colorHelpers } from '../utils/colors';
import { useTranslation } from 'react-i18next';
import { useCustomers } from '../hooks/useCustomers';
import { useImportExport } from '../hooks/useImportExport';
import { RoleGuard } from '../components/RoleGuards';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';

// Customers are accessible to Admin/Super Admin and to Operations Manager
// (which may create/edit customers). Delete stays Admin/Super-Admin-only via
// the per-row checkDeleteAccess gate.
const CUSTOMER_ACCESS_ROLES = [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.OPERATIONS_MANAGER];

/**
 * CustomersPage
 * - Page wrapper that composes customer list, filters, import/export and CRUD modals.
 * - Uses `useCustomers` for data operations and `useImportExport` for import/export flows.
 */
const CustomersPage = () => {
  const { t } = useTranslation();
  const { userRole } = useRole();
  const {
    customers,
    loading,
    error,
    selectedCustomers,
    filters,
    hasSelection,
    selectionCount,
    totalCount,
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    goToPage,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    bulkDeleteCustomers,
    toggleCustomerStatus,
    getCustomerById,
    importCustomers,
    exportCustomers,
    selectCustomer,
    selectAllCustomers,
    clearSelection,
    fetchCustomers,
    updateFilters,
  } = useCustomers();

  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'approvals' (super-admin)
  const [alertMessage, setAlertMessage] = useState(null);
  const isSuperAdmin = userRole === USER_ROLES.SUPER_ADMIN;
  const [showDetails, setShowDetails] = useState(false);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showWarehouseForm, setShowWarehouseForm] = useState(false);
  const [newCustomerId, setNewCustomerId] = useState(null);
  const [showWarehouseList, setShowWarehouseList] = useState(false);
  const [warehouseListCustomer, setWarehouseListCustomer] = useState(null);

  const { showError } = useErrorDialog();

  const { importing, exporting, importError, importSuccess, handleImport, handleExport, resetImportState } = useImportExport({
    onImport: importCustomers,
    onExport: exportCustomers,
  });

  const importErrorMessage = useMemo(() => {
    if (!importError) return null;
    const errorDictionary = {
      NO_FILE: t('customers.import.errors.noFile'),
      INVALID_TYPE: t('customers.import.errors.invalidType'),
      SIZE_EXCEEDED: t('customers.import.errors.uploadFailed'),
      IMPORT_FAILED: t('customers.import.errors.uploadFailed'),
      UPLOAD_FAILED: t('customers.import.errors.uploadFailed'),
    };
    return errorDictionary[importError] || importError;
  }, [importError, t]);

  // Controlled input state for filters (drives both the UI and API calls)
  const [uiFilters, setUiFilters] = useState({
    search: filters?.search ?? '',
    customerType: filters?.customerType ?? '',
    state: filters?.state ?? '',
    isActive: filters?.isActive ?? true,
    onboardingStatus: filters?.onboardingStatus ?? '',
  });

  const searchDebounceRef = useRef(null);

  // All filtering is server-side; just pass customers through
  const displayedCustomers = Array.isArray(customers) ? customers : [];

  /** Debounced search — updates input immediately, hits API after 400ms */
  const handleSearchChange = useCallback((value) => {
    setUiFilters((prev) => ({ ...prev, search: value }));
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      updateFilters({ search: value });
    }, 400);
  }, [updateFilters]);

  /** Update an API-driven filter immediately */
  const handleFilterChange = useCallback((filterKey, value) => {
    setUiFilters((prev) => ({ ...prev, [filterKey]: value }));
    updateFilters({ [filterKey]: value });
  }, [updateFilters]);

  /** Reset all filters and re-fetch */
  const resetFilters = useCallback(() => {
    const defaults = { search: '', customerType: '', state: '', isActive: null, onboardingStatus: '' };
    setUiFilters(defaults);
    clearTimeout(searchDebounceRef.current);
    updateFilters(defaults);
  }, [updateFilters]);

  /** Open the customer creation modal */
  const openCreateForm = () => {
    setEditingCustomer(null);
    setShowForm(true);
  };

  /** Open edit modal and preload customer data from API (with loading) */
  const openEditForm = async (customerId) => {
    const fetched = await getCustomerById(customerId, { withLoading: true });
    if (!fetched) {
      // 404 / deleted / failed fetch. Do NOT open the form — with editingCustomer
      // null it renders as a blank "Add Customer" and submit would CREATE a brand-new
      // duplicate instead of editing. The hook already surfaces the error via the
      // global error dialog.
      return;
    }
    setEditingCustomer(fetched);
    setShowForm(true);
  };

  /** Submit handler used by CustomerForm for create/update */
  const handleFormSubmit = async (formValues) => {
    const action = editingCustomer ? updateCustomer : createCustomer;
    const result = editingCustomer
      ? await action(editingCustomer.id, formValues)
      : await action(formValues);

    if (result?.success) {
      setShowForm(false);
      setEditingCustomer(null);
      if (!editingCustomer && result.data?.id) {
        setNewCustomerId(result.data.id);
        setShowWarehouseForm(true);
      }
    }
    return result;
  };

  /** Open the details modal and fetch the latest customer data (no global loading) */
  const openDetails = async (customerId) => {
    setDetailsLoading(true);
    setShowDetails(true);
    setViewingCustomer(null);
    try {
      const data = await getCustomerById(customerId, { withLoading: false });
      setViewingCustomer(data);
    } catch (err) {
      showError(err.message || t('common.alerts.errorMessage'), t('customers.fallbacks.errorTitle'));
    } finally {
      setDetailsLoading(false);
    }
  };

  /** Prepare and open single-delete confirmation dialog */
  const openDeleteConfirm = (customerId) => {
    const customer = customers.find((item) => item.id === customerId);
    const name = customer?.companyName || customer?.ownerName || t('customers.fallbacks.customerName');
    setConfirmState({
      type: 'delete',
      customerId,
      title: t('customers.confirm.deleteSingle.title'),
      message: t('customers.confirm.deleteSingle.message', { name }),
      confirmLabel: t('customers.confirm.buttons.delete'),
      confirmVariant: 'danger',
    });
  };

  /** Prepare and open bulk-delete confirmation dialog */
  const openBulkDeleteConfirm = () => {
    setConfirmState({
      type: 'bulkDelete',
      title: t('customers.confirm.deleteMultiple.title'),
      message: t('customers.confirm.deleteMultiple.message', { count: selectionCount }),
      confirmLabel: t('customers.confirm.buttons.delete'),
      confirmVariant: 'danger',
    });
  };

  /** Open confirm dialog for toggling active/inactive status of a customer */
  const openToggleConfirm = (customerId, nextStatus) => {
    const customer = customers.find((item) => item.id === customerId);
    const name = customer?.companyName || customer?.ownerName || t('customers.fallbacks.customerName');
    setConfirmState({
      type: 'toggle',
      customerId,
      nextStatus,
      title: nextStatus ? t('customers.confirm.activate.title') : t('customers.confirm.deactivate.title'),
      message: nextStatus ? t('customers.confirm.activate.message', { name }) : t('customers.confirm.deactivate.message', { name }),
      confirmLabel: nextStatus ? t('customers.confirm.buttons.activate') : t('customers.confirm.buttons.deactivate'),
      confirmVariant: nextStatus ? 'success' : 'danger',
    });
  };

  /** Execute the confirmed action (delete, bulk delete, toggle status) */
  const handleConfirm = async () => {
    if (!confirmState) return;
    setConfirmLoading(true);
    try {
      if (confirmState.type === 'delete') {
        await deleteCustomer(confirmState.customerId);
      } else if (confirmState.type === 'bulkDelete') {
        await bulkDeleteCustomers();
      } else if (confirmState.type === 'toggle') {
        await toggleCustomerStatus(confirmState.customerId, confirmState.nextStatus);
      }
      setConfirmState(null);
      clearSelection();
    } finally {
      setConfirmLoading(false);
    }
  };

  // Show global error dialog when hook reports an error
  React.useEffect(() => {
    if (error) {
      showError(error, t('customers.fallbacks.errorTitle'));
    }
  }, [error, showError, t]);

  const pageActions = [
    ...(hasSelection
      ? [
        {
          key: 'delete',
          label: '',
          icon: faTrash,
          variant: 'danger',
          onClick: openBulkDeleteConfirm,
          tooltip: t('customers.actions.buttons.deleteSelected'),
          ariaLabel: t('customers.actions.buttons.deleteSelected'),
        },
      ]
      : []),
    {
      key: 'import',
      label: '',
      icon: faFileImport,
      variant: 'secondary',
      onClick: () => setShowImportModal(true),
      loading: importing,
      disabled: importing,
      tooltip: t('customers.actions.buttons.import'),
      ariaLabel: t('customers.actions.buttons.import'),
      textColor: UI_COLORS.TEXT.WHITE,
    },
    {
      key: 'export',
      label: '',
      icon: faFileExport,
      variant: 'secondary',
      onClick: handleExport,
      loading: exporting,
      disabled: exporting,
      tooltip: t('customers.actions.buttons.export'),
      ariaLabel: t('customers.actions.buttons.export'),
      textColor: UI_COLORS.TEXT.WHITE,
    },
    {
      key: 'refresh',
      label: '',
      variant: 'ghost',
      onClick: () => fetchCustomers({ force: true }),
      icon: faSyncAlt,
      loading,
      disabled: loading,
      tooltip: t('customers.actions.buttons.refresh'),
      ariaLabel: t('customers.actions.buttons.refresh'),
    },
    // Invite (super-admin only) — the self-service onboarding entry point.
    ...(isSuperAdmin ? [{
      key: 'invite',
      label: '',
      variant: 'secondary',
      onClick: () => setShowInviteModal(true),
      icon: faUserPlus,
      tooltip: t('customers.invite.title'),
      ariaLabel: t('customers.invite.title'),
      textColor: UI_COLORS.TEXT.WHITE,
    }] : []),
    {
      key: 'add',
      label: '',
      variant: 'primary',
      onClick: openCreateForm,
      icon: faPlus,
      tooltip: t('customers.actions.buttons.add'),
      ariaLabel: t('customers.actions.buttons.add'),
    },
  ];

  const stateOptions = useMemo(() => [
    { value: '', label: t('customers.actions.filters.state.placeholder') },
    ...t('locations.states', { returnObjects: true }),
  ], [t]);

  const filterElements = [
    <div key="type" className="flex items-center gap-2">
      <FontAwesomeIcon icon={faFilter} style={{ color: UI_COLORS.TEXT.MUTED }} />
      <select
        value={uiFilters.customerType}
        onChange={(event) => handleFilterChange('customerType', event.target.value)}
        className="rounded-xl border px-3 py-2 text-sm"
        style={{
          backgroundColor: UI_COLORS.FORMS.INPUT.background,
          borderColor: UI_COLORS.FORMS.INPUT.border,
          color: UI_COLORS.FORMS.INPUT.text,
        }}
      >
        <option value="">{t('customers.actions.filters.customerType.options.all')}</option>
        <option value="Customer">{t('customers.actions.filters.customerType.options.customer')}</option>
        <option value="Supplier">{t('customers.actions.filters.customerType.options.supplier')}</option>
      </select>
    </div>,
    // Without this the list is approved-only (the API default), so invited and rejected
    // customers are reachable solely through the Approvals queue.
    <div key="onboarding" className="flex items-center gap-2">
      <select
        value={uiFilters.onboardingStatus}
        onChange={(event) => handleFilterChange('onboardingStatus', event.target.value)}
        className="rounded-xl border px-3 py-2 text-sm"
        style={{
          backgroundColor: UI_COLORS.FORMS.INPUT.background,
          borderColor: UI_COLORS.FORMS.INPUT.border,
          color: UI_COLORS.FORMS.INPUT.text,
        }}
      >
        <option value="">{t('customers.actions.filters.onboarding.options.approved')}</option>
        <option value="all">{t('customers.actions.filters.onboarding.options.all')}</option>
        <option value="invited">{t('customers.actions.filters.onboarding.options.invited')}</option>
        <option value="profile_submitted">{t('customers.actions.filters.onboarding.options.profileSubmitted')}</option>
        <option value="rejected">{t('customers.actions.filters.onboarding.options.rejected')}</option>
      </select>
    </div>,
    <div key="state" style={{ minWidth: '180px' }}>
      <SearchableSelect
        value={uiFilters.state}
        options={stateOptions}
        placeholder={t('customers.actions.filters.state.placeholder')}
        onChange={(event) => handleFilterChange('state', event.target.value)}
      />
    </div>,
    <select
      key="status"
      value={uiFilters.isActive === null || uiFilters.isActive === undefined ? '' : String(uiFilters.isActive)}
      onChange={(event) => {
        const val = event.target.value;
        handleFilterChange('isActive', val === '' ? null : val === 'true');
      }}
      className="rounded-xl border px-3 py-2 text-sm"
      style={{
        backgroundColor: UI_COLORS.FORMS.INPUT.background,
        borderColor: UI_COLORS.FORMS.INPUT.border,
        color: UI_COLORS.FORMS.INPUT.text,
      }}
    >
      <option value="">{t('customers.actions.filters.status.options.all')}</option>
      <option value="true">{t('customers.actions.filters.status.options.active')}</option>
      <option value="false">{t('customers.actions.filters.status.options.inactive')}</option>
    </select>,
    <button
      key="reset"
      type="button"
      onClick={resetFilters}
      className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold"
      style={{
        backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
        color: UI_COLORS.TEXT.MUTED,
      }}
    >
      <FontAwesomeIcon icon={faTimesCircle} />
      {t('customers.actions.filters.reset')}
    </button>,
  ];

  return (
    <RoleGuard roles={CUSTOMER_ACCESS_ROLES}>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {t('customers.header.title')}
            </h1>
            <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {t('customers.header.subtitle')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div
              className="flex items-center gap-3 rounded-2xl border px-4 py-3"
              style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
            >
              <div
                className="flex h-12 w-12 items-center justify-center rounded-full"
                style={{
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.15),
                  color: UI_COLORS.BUTTONS.PRIMARY.background,
                }}
              >
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                  {t('customers.header.totalCard.label')}
                </p>
                <p className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                  {totalCount}
                </p>
              </div>
            </div>
            {hasSelection && (
              <div
                className="rounded-2xl border px-4 py-3"
                style={{
                  borderColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.4),
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1),
                }}
              >
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.STATUS.INFO }}>
                  {t('customers.header.selectedCard.label')}
                </p>
                <p className="text-xl font-bold" style={{ color: UI_COLORS.STATUS.INFO }}>
                  {selectionCount}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* All customers vs Approvals queue (super-admin only) */}
        {isSuperAdmin && (
          <div className="flex gap-2 mb-4">
            {[{ id: 'all', label: t('customers.tabs.all') }, { id: 'approvals', label: t('customers.tabs.approvals') }].map((tab) => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className="px-4 py-1.5 rounded-full text-sm font-semibold transition-all"
                  style={{
                    color: active ? UI_COLORS.BUTTONS.PRIMARY.text : UI_COLORS.TEXT.SECONDARY,
                    backgroundColor: active ? UI_COLORS.BUTTONS.PRIMARY.background : colorHelpers.withOpacity(UI_COLORS.BRAND.SECONDARY, 0.06),
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {activeTab === 'approvals' && isSuperAdmin ? (
          <CustomerApprovalsTab
            onViewCustomer={openDetails}
            onAlertMessage={setAlertMessage}
            onCustomerChanged={() => fetchCustomers({ force: true })}
          />
        ) : (
        <>
        <ActionToolbar
          showSearch
          searchValue={uiFilters.search}
          onSearchChange={handleSearchChange}
          searchPlaceholder={t('customers.actions.searchPlaceholder')}
          searchAriaLabel={t('customers.actions.searchAriaLabel')}
          filters={filterElements}
          actions={pageActions}
        />

        <div className="mt-6">
          <CustomersTable
            customers={displayedCustomers}
            loading={loading}
            selectedCustomers={selectedCustomers}
            onSelectCustomer={selectCustomer}
            onSelectAll={selectAllCustomers}
            onViewCustomer={openDetails}
            onEditCustomer={openEditForm}
            onDeleteCustomer={openDeleteConfirm}
            onToggleStatus={openToggleConfirm}
            onViewWarehouses={(customer) => {
              setWarehouseListCustomer(customer);
              setShowWarehouseList(true);
            }}
          />
        </div>

        {totalPages > 1 && (
          <div
            className="mt-4 flex items-center justify-between rounded-xl border px-6 py-3"
            style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}
          >
            <span className="text-sm" style={{ color: '#6b7280' }}>
              {t('customers.pagination.pageOfTotal', { current: currentPage, total: totalPages, count: totalCount })}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={prevPage}
                disabled={currentPage <= 1 || loading}
                className="rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6b7280' }}
              >
                {t('customers.pagination.previous')}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p);
                  acc.push(p);
                  return acc;
                }, [])
                .map((item) =>
                  typeof item === 'string' ? (
                    <span key={item} className="px-2 text-sm" style={{ color: '#6b7280' }}>
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => goToPage(item)}
                      disabled={loading}
                      className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: item === currentPage ? '#0066cc' : 'transparent',
                        color: item === currentPage ? '#fff' : '#374151',
                        border: item === currentPage ? 'none' : '1px solid rgba(0,0,0,0.15)',
                      }}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                type="button"
                onClick={nextPage}
                disabled={currentPage >= totalPages || loading}
                className="rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: '#0066cc', color: '#fff' }}
              >
                {t('customers.pagination.next')}
              </button>
            </div>
          </div>
        )}
        </>
        )}

        {showForm && (
          <CustomerForm
            isOpen={showForm}
            customer={editingCustomer}
            onClose={() => {
              setShowForm(false);
              setEditingCustomer(null);
            }}
            onSubmit={handleFormSubmit}
            loading={loading}
          />
        )}

        {showWarehouseForm && (
          <CustomerWarehouseForm
            customerId={newCustomerId}
            isOpen={showWarehouseForm}
            onClose={() => {
              setShowWarehouseForm(false);
              setNewCustomerId(null);
            }}
          />
        )}

        {showImportModal && (
          <CustomerImportModal
            isOpen={showImportModal}
            onClose={() => {
              resetImportState();
              setShowImportModal(false);
            }}
            onImport={handleImport}
            importing={importing}
            importError={importErrorMessage}
            importSuccess={importSuccess}
          />
        )}

        {showDetails && (
          <CustomerDetails
            isOpen={showDetails}
            onClose={() => {
              setShowDetails(false);
              setViewingCustomer(null);
            }}
            customer={viewingCustomer}
            loading={detailsLoading}
            userRole={userRole}
            onAddWarehouse={(customerId) => {
              setShowDetails(false);
              setNewCustomerId(customerId);
              setShowWarehouseForm(true);
            }}
          />
        )}

        {showWarehouseList && (
          <CustomerWarehouseListModal
            isOpen={showWarehouseList}
            onClose={() => {
              setShowWarehouseList(false);
              setWarehouseListCustomer(null);
            }}
            customerId={warehouseListCustomer?.id}
            customerName={warehouseListCustomer?.companyName || warehouseListCustomer?.ownerName}
          />
        )}

        {confirmState && (
          <ConfirmDialog
            isOpen
            title={confirmState.title}
            message={confirmState.message}
            confirmLabel={confirmState.confirmLabel}
            confirmVariant={confirmState.confirmVariant}
            loading={confirmLoading}
            loadingLabel={confirmState.confirmLabel}
            onConfirm={handleConfirm}
            onCancel={() => setConfirmState(null)}
          />
        )}

        {showInviteModal && (
          <CustomerInviteModal
            isOpen={showInviteModal}
            onClose={() => setShowInviteModal(false)}
            onInvited={() => fetchCustomers({ force: true })}
          />
        )}

        {alertMessage && (
          <MessageAlert
            type={alertMessage.type}
            title={alertMessage.title}
            message={alertMessage.message}
            onClose={() => setAlertMessage(null)}
          />
        )}
      </div>
    </RoleGuard>
  );
};

export default CustomersPage;
